import type { BuilderPanelId } from '@/lib/builder-preferences';

export type CommandScope = 'builder' | 'global';

export type Keybinding = {
  key: string;
  primary?: boolean;
  shift?: boolean;
};

export type EditorCommandContext = {
  surface?: 'dashboard' | 'builder';
  pathname?: string;
  navigate?: (path: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  canDelete: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  canDuplicate?: boolean;
  canCopy?: boolean;
  canCut?: boolean;
  canPaste?: boolean;
  undo: () => void;
  redo: () => void;
  deleteSelection: () => void;
  clearSelection: () => void;
  /** Step OUT one level: select the parent container of the current anchor, or clear the selection
   *  when there is no parent left (the anchor is the step root). Optional so the dashboard context
   *  can omit it; the Escape command falls back to `clearSelection`. */
  selectParent?: () => void;
  suppressBrowserSave: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  toggleOutlines: () => void;
  openShortcutReference: () => void;
  duplicateSelection?: () => void;
  copySelection?: () => void;
  cutSelection?: () => void;
  pasteSelection?: () => void;
  activeBuilderPanel?: BuilderPanelId;
  openBuilderPanel?: (panel: BuilderPanelId) => void;
  /** Variant ops (ADR 0015): open the switcher's dialogs rather than mutating directly, so the
   *  palette path and the header path share one naming/confirm flow. */
  canCreateVariant?: boolean;
  canDeleteVariant?: boolean;
  openCreateVariant?: () => void;
  openDeleteVariant?: () => void;
};

export type EditorCommand = {
  id: string;
  label: string;
  description: string;
  category: string;
  scope: CommandScope;
  keywords: readonly string[];
  keybindings: readonly Keybinding[];
  telemetry: string;
  enabled: (context: EditorCommandContext) => boolean;
  disabledReason?: (context: EditorCommandContext) => string;
  execute: (context: EditorCommandContext) => void;
};

const always = (): boolean => true;
const noop = (): void => {};

export function globalCommandContext(pathname: string, navigate: (path: string) => void): EditorCommandContext {
  return {
    surface: 'dashboard',
    pathname,
    navigate,
    canUndo: false,
    canRedo: false,
    canDelete: false,
    canZoomIn: false,
    canZoomOut: false,
    undo: noop,
    redo: noop,
    deleteSelection: noop,
    clearSelection: noop,
    suppressBrowserSave: noop,
    zoomIn: noop,
    zoomOut: noop,
    resetZoom: noop,
    toggleOutlines: noop,
    openShortcutReference: noop,
  };
}

/**
 * The command descriptors are deliberately data, not React callbacks. Buttons, keyboard dispatch,
 * the shortcut reference, the future command palette, and AI eligibility all read this same list.
 * Execution still receives a freshly-resolved context, so an open menu cannot leave a stale command
 * enabled after the document changes.
 */
export const EDITOR_COMMANDS = [
  {
    id: 'global.open-command-palette',
    label: 'Open command palette',
    description: 'Search navigation and available actions.',
    category: 'Navigation',
    scope: 'global',
    keywords: ['spotlight', 'search', 'commands'],
    keybindings: [{ key: 'k', primary: true }],
    telemetry: 'command_palette_open',
    enabled: always,
    execute: () => {},
  },
  ...([
    ['/home', 'Go to Home', ['dashboard']],
    ['/install', 'Go to Install', ['sdk', 'setup']],
    ['/experiences', 'Go to Experiences', ['builder', 'content']],
    ['/campaigns', 'Go to Campaigns', ['delivery', 'publish']],
    ['/segments', 'Go to Audiences', ['segments', 'targeting']],
    ['/assets', 'Go to Assets', ['images', 'fonts', 'media']],
    ['/theme', 'Go to Theme', ['tokens', 'colors', 'fonts']],
    ['/settings', 'Go to Settings', ['account', 'workspace']],
  ] as const).map(([path, label, keywords]) => ({
    id: `global.navigate.${path.slice(1)}`,
    label,
    description: `Open ${label.slice(6)}.`,
    category: 'Navigation',
    scope: 'global' as const,
    keywords,
    keybindings: [],
    telemetry: `navigate_${path.slice(1)}`,
    enabled: (context: EditorCommandContext) => context.pathname !== path && Boolean(context.navigate),
    disabledReason: () => 'Already here.',
    execute: (context: EditorCommandContext) => context.navigate?.(path),
  })),
  {
    id: 'builder.open-layers',
    label: 'Open Layers panel',
    description: 'Browse and arrange the current step structure.',
    category: 'Navigation',
    scope: 'builder',
    keywords: ['sidebar', 'tree', 'components'],
    keybindings: [],
    telemetry: 'builder_panel_layers_open',
    enabled: (context) => Boolean(context.openBuilderPanel) && context.activeBuilderPanel !== 'layers',
    disabledReason: () => 'Layers is already open.',
    execute: (context) => context.openBuilderPanel?.('layers'),
  },
  {
    id: 'builder.open-theme',
    label: 'Open Theme panel',
    description: 'Edit the current experience theme.',
    category: 'Navigation',
    scope: 'builder',
    keywords: ['tokens', 'colors', 'fonts', 'buttons'],
    keybindings: [],
    telemetry: 'builder_panel_theme_open',
    enabled: (context) => Boolean(context.openBuilderPanel) && context.activeBuilderPanel !== 'theme',
    disabledReason: () => 'Theme is already open.',
    execute: (context) => context.openBuilderPanel?.('theme'),
  },
  {
    id: 'builder.open-assets',
    label: 'Open Assets panel',
    description: 'Browse images and bundled fonts.',
    category: 'Navigation',
    scope: 'builder',
    keywords: ['media', 'images', 'fonts', 'library'],
    keybindings: [],
    telemetry: 'builder_panel_assets_open',
    enabled: (context) => Boolean(context.openBuilderPanel) && context.activeBuilderPanel !== 'assets',
    disabledReason: () => 'Assets is already open.',
    execute: (context) => context.openBuilderPanel?.('assets'),
  },
  {
    id: 'builder.undo',
    label: 'Undo',
    description: 'Undo the last document change.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['back', 'revert', 'history'],
    keybindings: [{ key: 'z', primary: true }],
    telemetry: 'builder_command_undo',
    enabled: (context) => context.canUndo,
    execute: (context) => context.undo(),
  },
  {
    id: 'builder.redo',
    label: 'Redo',
    description: 'Restore the last undone document change.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['forward', 'history'],
    keybindings: [{ key: 'z', primary: true, shift: true }],
    telemetry: 'builder_command_redo',
    enabled: (context) => context.canRedo,
    execute: (context) => context.redo(),
  },
  {
    id: 'builder.duplicate-selection',
    label: 'Duplicate selection',
    description: 'Duplicate the selected component subtrees.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['copy', 'clone', 'components'],
    keybindings: [{ key: 'd', primary: true }],
    telemetry: 'builder_command_duplicate_selection',
    enabled: (context) => Boolean(context.canDuplicate),
    disabledReason: () => 'Select a non-root component first.',
    execute: (context) => context.duplicateSelection?.(),
  },
  {
    id: 'builder.copy-selection',
    label: 'Copy selection',
    description: 'Copy selected component subtrees.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['clipboard', 'components'],
    keybindings: [{ key: 'c', primary: true }],
    telemetry: 'builder_command_copy_selection',
    enabled: (context) => Boolean(context.canCopy),
    execute: (context) => context.copySelection?.(),
  },
  {
    id: 'builder.cut-selection',
    label: 'Cut selection',
    description: 'Copy and remove selected component subtrees.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['clipboard', 'remove', 'components'],
    keybindings: [{ key: 'x', primary: true }],
    telemetry: 'builder_command_cut_selection',
    enabled: (context) => Boolean(context.canCut),
    execute: (context) => context.cutSelection?.(),
  },
  {
    id: 'builder.paste-selection',
    label: 'Paste components',
    description: 'Paste copied components at the current insertion point.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['clipboard', 'insert'],
    keybindings: [{ key: 'v', primary: true }],
    telemetry: 'builder_command_paste_selection',
    enabled: (context) => Boolean(context.canPaste),
    disabledReason: () => 'Copy components in PersonalizeHQ first.',
    execute: (context) => context.pasteSelection?.(),
  },
  {
    id: 'builder.delete-selection',
    label: 'Delete selection',
    description: 'Remove the selected component.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['remove', 'component', 'node'],
    keybindings: [{ key: 'Delete' }, { key: 'Backspace' }],
    telemetry: 'builder_command_delete_selection',
    enabled: (context) => context.canDelete,
    execute: (context) => context.deleteSelection(),
  },
  {
    id: 'builder.select-parent',
    label: 'Select parent',
    description: 'Step out one level to the parent container; clears the selection at the top level.',
    category: 'Selection',
    scope: 'builder',
    keywords: ['escape', 'deselect', 'parent', 'container', 'step out', 'up'],
    keybindings: [{ key: 'Escape' }],
    telemetry: 'builder_command_select_parent',
    enabled: always,
    // A canvas click selects the DEEPEST node under the pointer, so there is no gesture to reach the
    // container holding it (the exact reason "select the HView" was not obvious). Escape now walks up
    // the tree — child → … → root → cleared — instead of clearing in one jump. Falls back to a plain
    // clear where the surface has no parent-walk (the dashboard).
    execute: (context) => (context.selectParent ?? context.clearSelection)(),
  },
  {
    id: 'builder.new-variant',
    label: 'New variant',
    description: 'Duplicate the current steps as a new experiment arm.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['experiment', 'ab test', 'arm', 'variant'],
    keybindings: [],
    telemetry: 'builder_command_new_variant',
    enabled: (context) => Boolean(context.canCreateVariant),
    disabledReason: () => 'An experience may hold at most 7 variants.',
    execute: (context) => context.openCreateVariant?.(),
  },
  {
    id: 'builder.delete-variant',
    label: 'Delete variant',
    description: 'Remove the active variant and its steps.',
    category: 'Edit',
    scope: 'builder',
    keywords: ['experiment', 'arm', 'variant', 'remove'],
    keybindings: [],
    telemetry: 'builder_command_delete_variant',
    enabled: (context) => Boolean(context.canDeleteVariant),
    disabledReason: () => 'Focus a variant first — the original cannot be deleted.',
    execute: (context) => context.openDeleteVariant?.(),
  },
  {
    id: 'builder.save',
    label: 'Save draft',
    description: 'Drafts save automatically; this shortcut suppresses the browser Save dialog.',
    category: 'Document',
    scope: 'builder',
    keywords: ['autosave', 'draft'],
    keybindings: [{ key: 's', primary: true }],
    telemetry: 'builder_command_save',
    enabled: always,
    execute: (context) => context.suppressBrowserSave(),
  },
  {
    id: 'builder.zoom-in',
    label: 'Zoom in',
    description: 'Make the canvas larger.',
    category: 'View',
    scope: 'builder',
    keywords: ['canvas', 'scale', 'magnify'],
    keybindings: [{ key: '=', primary: true }],
    telemetry: 'builder_command_zoom_in',
    enabled: (context) => context.canZoomIn,
    execute: (context) => context.zoomIn(),
  },
  {
    id: 'builder.zoom-out',
    label: 'Zoom out',
    description: 'Make the canvas smaller.',
    category: 'View',
    scope: 'builder',
    keywords: ['canvas', 'scale', 'shrink'],
    keybindings: [{ key: '-', primary: true }],
    telemetry: 'builder_command_zoom_out',
    enabled: (context) => context.canZoomOut,
    execute: (context) => context.zoomOut(),
  },
  {
    id: 'builder.reset-zoom',
    label: 'Reset zoom',
    description: 'Return the canvas to 100%.',
    category: 'View',
    scope: 'builder',
    keywords: ['canvas', 'scale', 'actual size'],
    keybindings: [{ key: '0', primary: true }],
    telemetry: 'builder_command_reset_zoom',
    enabled: always,
    execute: (context) => context.resetZoom(),
  },
  {
    id: 'builder.toggle-outlines',
    label: 'Toggle layout outlines',
    description: 'Show or hide component layout boundaries.',
    category: 'View',
    scope: 'builder',
    keywords: ['boxes', 'bounds', 'layout'],
    keybindings: [],
    telemetry: 'builder_command_toggle_outlines',
    enabled: always,
    execute: (context) => context.toggleOutlines(),
  },
  {
    id: 'builder.shortcut-reference',
    label: 'Keyboard shortcuts',
    description: 'Open the searchable shortcut reference.',
    category: 'Help',
    scope: 'builder',
    keywords: ['help', 'keys', 'commands'],
    keybindings: [{ key: '/', primary: true }],
    telemetry: 'builder_command_shortcut_reference',
    enabled: always,
    execute: (context) => context.openShortcutReference(),
  },
] satisfies readonly EditorCommand[];

export type EditorCommandId = (typeof EDITOR_COMMANDS)[number]['id'];

export function commandById(id: EditorCommandId): EditorCommand {
  return EDITOR_COMMANDS.find((command) => command.id === id)!;
}

export function runEditorCommand(id: EditorCommandId, context: EditorCommandContext): boolean {
  const command = commandById(id);
  if (!command.enabled(context)) return false;
  command.execute(context);
  return true;
}

export function formatKeybinding(binding: Keybinding, apple = isApplePlatform()): string {
  const parts: string[] = [];
  if (binding.primary) parts.push(apple ? '⌘' : 'Ctrl');
  if (binding.shift) parts.push(apple ? '⇧' : 'Shift');
  const key = binding.key === ' ' ? 'Space' : binding.key.length === 1 ? binding.key.toUpperCase() : binding.key;
  parts.push(key);
  return parts.join(apple ? '' : '+');
}

export function isEditableCommandTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('input, textarea, select, [contenteditable]'));
}

export function hasOpenCommandOverlay(doc: Document): boolean {
  return Boolean(
    doc.querySelector('[role="listbox"], [role="dialog"], [role="menu"], [data-radix-popper-content-wrapper]')
  );
}

export function dispatchEditorKeydown(
  event: KeyboardEvent,
  context: EditorCommandContext,
  commands: readonly EditorCommand[] = EDITOR_COMMANDS
): EditorCommand | null {
  if (isEditableCommandTarget(event.target) || hasOpenCommandOverlay(event.view?.document ?? document)) return null;

  const command = commands.find(
    (candidate) =>
      candidate.scope === 'builder' &&
      candidate.enabled(context) && candidate.keybindings.some((binding) => keybindingMatches(event, binding))
  );
  if (!command) return null;
  event.preventDefault();
  command.execute(context);
  return command;
}

function keybindingMatches(event: KeyboardEvent, binding: Keybinding): boolean {
  const apple = isApplePlatform();
  const primary = apple ? event.metaKey : event.ctrlKey;
  const conflictingPrimary = apple ? event.ctrlKey : event.metaKey;
  const shiftedEquals = binding.key === '=' && event.key === '+' && event.shiftKey;
  return (
    (event.key.toLowerCase() === binding.key.toLowerCase() || shiftedEquals) &&
    primary === Boolean(binding.primary) &&
    !conflictingPrimary &&
    (shiftedEquals || event.shiftKey === Boolean(binding.shift)) &&
    !event.altKey
  );
}

function isApplePlatform(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}
